/**
 * AnalysisEventLines - Recharts ReferenceLine wrapper for event markers.
 *
 * Renders vertical dashed lines at event dates on measurement charts.
 * Lines are gray and subtle, with optional labels showing event type.
 * Multiple events on the same date are deduplicated into a single line.
 */
import { useMemo } from 'react';
import { ReferenceLine } from 'recharts';
import { parseISO, isWithinInterval } from 'date-fns';
import type { TaskEvent } from '@/hooks/useAnalysisData';
import { getEventConfig } from './constants';

/** Dash pattern for event lines */
const STROKE_DASHARRAY = '3 3';

interface EventLineData {
  date: string;
  type: string;
  timestamp: number;
  notes?: string;
}

interface GroupedEventLine {
  date: string;
  timestamp: number;
  types: string[];
  primaryColor: string; // Color of first event type
}

interface AnalysisEventLinesProps {
  /** List of selected event type names to display */
  selectedEvents: string[];
  /** All available maintenance events */
  events: TaskEvent[];
  /** Date range for filtering events (YYYY-MM-DD format) */
  dateRange: {
    start: string;
    end: string;
  };
  /** Whether to show event lines on the chart */
  showEventLines: boolean;
  /** Whether to show labels on event lines (default: false) */
  showLabels?: boolean;
}

/**
 * Check if an event date is within the date range (inclusive).
 */
function isEventInRange(
  eventDate: string,
  rangeStart: string,
  rangeEnd: string
): boolean {
  const eventDateParsed = parseISO(eventDate);
  const startDate = parseISO(rangeStart);
  const endDate = parseISO(rangeEnd);

  return isWithinInterval(eventDateParsed, { start: startDate, end: endDate });
}

/**
 * Convert a date string or datetime string to a timestamp.
 * When a full datetime is available, uses it for precise positioning.
 * Falls back to midday for date-only strings.
 */
function dateToTimestamp(dateStr: string, datetimeStr?: string): number {
  if (datetimeStr) {
    return new Date(datetimeStr).getTime();
  }
  return new Date(`${dateStr}T12:00:00`).getTime();
}

/**
 * Filter events based on selected event types and date range.
 * Returns events with timestamps for chart positioning.
 *
 * Exported for testing purposes.
 */
export function filterEventsForLines(
  selectedEvents: string[],
  events: TaskEvent[],
  dateRange: { start: string; end: string }
): EventLineData[] {
  if (selectedEvents.length === 0 || events.length === 0) {
    return [];
  }

  const selectedSet = new Set(selectedEvents);

  return events
    .filter((event) => {
      // Filter by selected event types
      if (!selectedSet.has(event.type)) return false;
      // Filter by date range
      if (!isEventInRange(event.date, dateRange.start, dateRange.end)) return false;
      return true;
    })
    .map((event) => ({
      ...event,
      timestamp: dateToTimestamp(event.date, event.datetime),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Group events by date to deduplicate lines.
 * Multiple events on the same date become a single line.
 */
function groupEventsByDate(events: EventLineData[]): GroupedEventLine[] {
  const dateMap = new Map<string, GroupedEventLine>();

  events.forEach((event) => {
    if (dateMap.has(event.date)) {
      const existing = dateMap.get(event.date)!;
      if (!existing.types.includes(event.type)) {
        existing.types.push(event.type);
      }
    } else {
      const config = getEventConfig(event.type);
      dateMap.set(event.date, {
        date: event.date,
        timestamp: event.timestamp,
        types: [event.type],
        primaryColor: config.color,
      });
    }
  });

  return Array.from(dateMap.values()).sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Generate label text for a grouped event line.
 */
function getLineLabel(groupedEvent: GroupedEventLine): string {
  if (groupedEvent.types.length === 1 && groupedEvent.types[0]) {
    return groupedEvent.types[0];
  }
  return `${groupedEvent.types.length} events`;
}

/**
 * Renders ReferenceLine components for each event date.
 *
 * Must be rendered as children of a Recharts chart component (LineChart, ComposedChart, etc.)
 * Returns a fragment containing ReferenceLine components, or null if disabled/no events.
 */
export function AnalysisEventLines({
  selectedEvents,
  events,
  dateRange,
  showEventLines,
  showLabels = false,
}: AnalysisEventLinesProps): JSX.Element | null {
  // Filter and group events
  const groupedLines = useMemo(() => {
    if (!showEventLines) return [];

    const filteredEvents = filterEventsForLines(selectedEvents, events, dateRange);
    return groupEventsByDate(filteredEvents);
  }, [selectedEvents, events, dateRange, showEventLines]);

  // Return null if disabled or no matching events
  if (!showEventLines || groupedLines.length === 0) {
    return null;
  }

  return (
    <>
      {groupedLines.map((line) => (
        <ReferenceLine
          key={`event-line-${line.date}`}
          x={line.timestamp}
          stroke={line.primaryColor}
          strokeDasharray={STROKE_DASHARRAY}
          strokeOpacity={0.6}
          label={
            showLabels
              ? {
                  value: getLineLabel(line),
                  position: 'top',
                  fill: line.primaryColor,
                  fontSize: 10,
                }
              : undefined
          }
        />
      ))}
    </>
  );
}
